<template>
  <div class="property-update-form">
    <div class="form-header text-center mb-4">
      <h4 class="text-primary">Update Property Information</h4>
      <p class="text-muted">Review and update your property details below</p>
    </div>

    <!-- Loading State -->
    <div v-if="loadingProperty" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading property details...</p>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Current Property Information Section -->
      <div class="current-info-section mb-4">
        <div class="section-header">
          <h5 class="text-primary mb-3">
            <i class="fas fa-home mr-2"></i>
            Current Property Information
          </h5>
        </div>

        <div v-if="!currentProperty || !currentProperty.LRNumber" class="alert alert-warning">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          No property data available. Please go back and search for a property.
        </div>

        <div v-else class="property-info-cards">
          <div class="row">
            <!-- Property Details Card -->
            <div class="col-md-6 mb-3">
              <div class="info-card">
                <div class="card-header">
                  <h6><i class="fas fa-building mr-2"></i>Property Details</h6>
                </div>
                <div class="card-body">
                  <div class="info-item">
                    <span class="label">LR Number:</span>
                    <span class="value">{{ currentProperty.LRNumber }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">UPN:</span>
                    <span class="value">{{ currentProperty.UPN || 'Not assigned' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Size:</span>
                    <span class="value">{{ currentProperty.percelSize }} {{ currentProperty.percelSize ? 'Hectares' : '' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Location:</span>
                    <span class="value">{{ currentProperty.percelAddress || 'Not specified' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Use:</span>
                    <span class="value">{{ currentProperty.useName || 'Not specified' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Zone:</span>
                    <span class="value">{{ currentProperty.zone || 'Not specified' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Owner Information Card -->
            <div class="col-md-6 mb-3">
              <div class="info-card">
                <div class="card-header">
                  <h6><i class="fas fa-user mr-2"></i>Owner Information</h6>
                </div>
                <div class="card-body">
                  <div class="info-item">
                    <span class="label">Name:</span>
                    <span class="value">{{ currentProperty.ownerNames }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">ID Number:</span>
                    <span class="value">{{ currentProperty.ownerID || 'Not provided' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Phone:</span>
                    <span class="value">{{ currentProperty.ownerPhoneNo || 'Not provided' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Email:</span>
                    <span class="value">{{ currentProperty.ownerEmail || 'Not provided' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">PIN Number:</span>
                    <span class="value">{{ currentProperty.pinNumber || 'Not provided' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Physical Address:</span>
                    <span class="value">{{ currentProperty.physicalAddress || 'Not provided' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Location Details Card -->
            <div class="col-md-6 mb-3">
              <div class="info-card">
                <div class="card-header">
                  <h6><i class="fas fa-map-marker-alt mr-2"></i>Location Details</h6>
                </div>
                <div class="card-body">
                  <div class="info-item">
                    <span class="label">Sub-County:</span>
                    <span class="value">{{ currentProperty.subCountyName }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Ward:</span>
                    <span class="value">{{ currentProperty.wardName }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Town:</span>
                    <span class="value">{{ currentProperty.town || 'Not specified' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">P.O. Box:</span>
                    <span class="value">{{ currentProperty.boxNo || 'Not specified' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Postal Code:</span>
                    <span class="value">{{ currentProperty.postalCode || 'Not specified' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Information Card -->
            <div class="col-md-6 mb-3">
              <div class="info-card">
                <div class="card-header">
                  <h6><i class="fas fa-dollar-sign mr-2"></i>Financial Information</h6>
                </div>
                <div class="card-body">
                  <div class="info-item">
                    <span class="label">Outstanding Arrears:</span>
                    <span class="value text-danger font-weight-bold">KES {{ formatCurrency(currentProperty.outstandingArrears) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Annual Charges:</span>
                    <span class="value text-primary font-weight-bold">KES {{ formatCurrency(currentProperty.annualCharges) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Status:</span>
                    <span class="value">
                      <span :class="currentProperty.status === 'Active' ? 'badge badge-success' : 'badge badge-warning'">
                        {{ currentProperty.status }}
                      </span>
                    </span>
                  </div>
                  <div class="info-item">
                    <span class="label">Created Date:</span>
                    <span class="value">{{ formatDate(currentProperty.dateCreated) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">Created By:</span>
                    <span class="value">{{ currentProperty.createdBy }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Update Form Section -->
      <div class="update-form-section">
        <div class="section-header">
          <h5 class="text-primary mb-3">
            <i class="fas fa-edit mr-2"></i>
            Update Property Details
          </h5>
        </div>

        <form @submit.prevent="submitUpdate" class="update-form">
          <!-- Update Type Selection -->
          <div class="form-section">
            <h6 class="section-title">
              <i class="fas fa-list-check text-primary mr-2"></i>
              What would you like to update?
            </h6>
            <div class="update-type-selection">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="updateTypes.ownerInfo" id="updateOwner">
                    <label class="form-check-label" for="updateOwner">
                      <strong>Owner Information</strong><br>
                      <small class="text-muted">Name, contact details, ID number</small>
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="updateTypes.propertyDetails" id="updateProperty">
                    <label class="form-check-label" for="updateProperty">
                      <strong>Property Details</strong><br>
                      <small class="text-muted">Address, use, improvements</small>
                    </label>
                  </div>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-md-6">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="updateTypes.ownershipTransfer" id="updateOwnership">
                    <label class="form-check-label" for="updateOwnership">
                      <strong>Ownership Transfer</strong><br>
                      <small class="text-muted">Change of ownership details</small>
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="updateTypes.propertyImprovements" id="updateImprovements">
                    <label class="form-check-label" for="updateImprovements">
                      <strong>Property Improvements</strong><br>
                      <small class="text-muted">New buildings, structures, developments</small>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Owner Information Update -->
          <div v-if="updateTypes.ownerInfo" class="form-section">
            <h6 class="section-title">
              <i class="fas fa-user text-info mr-2"></i>
              Owner Information Updates
            </h6>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Full Name *</label>
                  <input
                      type="text"
                      class="form-control"
                      v-model="updateForm.ownerNames"
                      :placeholder="currentProperty.ownerNames || 'Enter full name'"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">ID/Passport Number</label>
                  <input
                      type="text"
                      class="form-control"
                      v-model="updateForm.ownerID"
                      :placeholder="currentProperty.ownerID || 'Enter ID number'"
                  >
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Phone Number *</label>
                  <input
                      type="tel"
                      class="form-control"
                      v-model="updateForm.ownerPhoneNo"
                      :placeholder="currentProperty.ownerPhoneNo || '0700000000'"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Email Address</label>
                  <input
                      type="email"
                      class="form-control"
                      v-model="updateForm.ownerEmail"
                      :placeholder="currentProperty.ownerEmail || 'your.email@example.com'"
                  >
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Physical Address</label>
              <textarea
                  class="form-control"
                  v-model="updateForm.physicalAddress"
                  rows="2"
                  :placeholder="currentProperty.physicalAddress || 'Enter your physical address'"
              ></textarea>
            </div>
          </div>

          <!-- Property Details Update -->
          <div v-if="updateTypes.propertyDetails" class="form-section">
            <h6 class="section-title">
              <i class="fas fa-home text-success mr-2"></i>
              Property Details Updates
            </h6>
            <div class="row">
              <div class="col-md-8">
                <div class="form-group">
                  <label class="form-label">Property Address/Location</label>
                  <input
                      type="text"
                      class="form-control"
                      v-model="updateForm.percelAddress"
                      :placeholder="currentProperty.percelAddress || 'Enter property address'"
                  >
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label class="form-label">Property Use</label>
                  <select class="form-control" v-model="updateForm.useName">
                    <option value="">Select use type</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Industrial">Industrial</option>
                    <option value="Agricultural">Agricultural</option>
                    <option value="Mixed-Use">Mixed-Use</option>
                    <option value="Institutional">Institutional</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Ownership Transfer -->
          <div v-if="updateTypes.ownershipTransfer" class="form-section">
            <h6 class="section-title">
              <i class="fas fa-exchange-alt text-warning mr-2"></i>
              Ownership Transfer Details
            </h6>
            <div class="alert alert-info">
              <i class="fas fa-info-circle mr-2"></i>
              <strong>Note:</strong> Ownership transfers require additional documentation and verification.
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">New Owner Name *</label>
                  <input
                      type="text"
                      class="form-control"
                      v-model="updateForm.newOwnerName"
                      placeholder="Enter new owner's full name"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">New Owner ID Number *</label>
                  <input
                      type="text"
                      class="form-control"
                      v-model="updateForm.newOwnerID"
                      placeholder="Enter new owner's ID number"
                  >
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Transfer Date *</label>
                  <input
                      type="date"
                      class="form-control"
                      v-model="updateForm.transferDate"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Transfer Type</label>
                  <select class="form-control" v-model="updateForm.transferType">
                    <option value="">Select transfer type</option>
                    <option value="Sale">Sale</option>
                    <option value="Gift">Gift</option>
                    <option value="Inheritance">Inheritance</option>
                    <option value="Court Order">Court Order</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Property Improvements -->
          <div v-if="updateTypes.propertyImprovements" class="form-section">
            <h6 class="section-title">
              <i class="fas fa-tools text-primary mr-2"></i>
              Property Improvements
            </h6>
            <div class="form-group">
              <label class="form-label">Describe the improvements made to your property</label>
              <textarea
                  class="form-control"
                  v-model="updateForm.improvements"
                  rows="4"
                  placeholder="Describe any new buildings, structures, renovations, or developments on your property"
              ></textarea>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Approximate Cost of Improvements</label>
                  <input
                      type="number"
                      class="form-control"
                      v-model="updateForm.improvementCost"
                      placeholder="Enter amount in KES"
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-label">Completion Date</label>
                  <input
                      type="date"
                      class="form-control"
                      v-model="updateForm.improvementDate"
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Document Upload Section -->
          <div class="form-section">
            <h6 class="section-title">
              <i class="fas fa-paperclip text-secondary mr-2"></i>
              Supporting Documents
            </h6>
            <p class="text-muted small">Upload documents to support your update request (Optional but recommended)</p>

            <DocumentUpload
                @files-uploaded="handleDocumentUpload"
                :allowed-types="['pdf', 'jpg', 'jpeg', 'png']"
                :max-size="5"
            />
          </div>

          <!-- Additional Notes -->
          <div class="form-section">
            <h6 class="section-title">
              <i class="fas fa-comment text-info mr-2"></i>
              Additional Information
            </h6>
            <div class="form-group">
              <label class="form-label">Additional Notes or Comments</label>
              <textarea
                  class="form-control"
                  v-model="updateForm.notes"
                  rows="3"
                  placeholder="Any additional information you'd like to provide regarding this update request"
              ></textarea>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="form-actions">
            <div class="row">
              <div class="col-md-4">
                <button
                    type="button"
                    class="btn btn-outline-secondary btn-block"
                    @click="$emit('back-to-verification')"
                >
                  <i class="fas fa-arrow-left mr-2"></i>
                  Back
                </button>
              </div>
              <div class="col-md-4">
                <button
                    type="button"
                    class="btn btn-outline-primary btn-block"
                    @click="saveAsDraft"
                >
                  <i class="fas fa-save mr-2"></i>
                  Save as Draft
                </button>
              </div>
              <div class="col-md-4">
                <button
                    type="submit"
                    class="btn btn-success btn-block"
                    :disabled="loading || !hasUpdates"
                >
                  <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
                  <i v-else class="fas fa-paper-plane mr-2"></i>
                  {{ loading ? 'Submitting...' : 'Submit Request' }}
                </button>
              </div>
            </div>
          </div>

        </form>
      </div>
    </div>

  </div>
</template>

<script>
import DocumentUpload from './DocumentUpload.vue'
import { execute, landrate } from '@/api'

export default {
  name: 'PropertyUpdateForm',
  components: {
    DocumentUpload
  },
  props: {
    property: {
      type: Object,
      required: true,
      default: () => ({})
    },
    verifiedOwner: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      loadingProperty: false,
      propertyData: null, // Store fetched property data
      updateTypes: {
        ownerInfo: false,
        propertyDetails: false,
        ownershipTransfer: false,
        propertyImprovements: false
      },
      updateForm: {
        // Owner info
        ownerNames: '',
        ownerID: '',
        ownerPhoneNo: '',
        ownerEmail: '',
        physicalAddress: '',

        // Property details
        percelAddress: '',
        useName: '',

        // Ownership transfer
        newOwnerName: '',
        newOwnerID: '',
        transferDate: '',
        transferType: '',

        // Improvements
        improvements: '',
        improvementCost: '',
        improvementDate: '',

        // Additional
        notes: ''
      },
      uploadedDocuments: []
    }
  },
  computed: {
    hasUpdates() {
      return Object.values(this.updateTypes).some(Boolean)
    },
    // Use fetched property data if available, otherwise use passed prop
    currentProperty() {
      return this.propertyData || this.property || {}
    }
  },
  methods: {
    async fetchPropertyDetails() {
      if (!this.property.LRNumber) {
        console.error('No LR Number provided')
        return
      }

      this.loadingProperty = true

      try {
        const formData = new FormData()
        formData.append('function', 'searchPropertyForUpdate')
        formData.append('lrNumber', this.property.LRNumber)

        console.log('Fetching property details for LR:', this.property.LRNumber)

        const response = await execute(formData, landrate)

        console.log('API Response:', response)

        if (response.data && response.data.success && response.data.data && response.data.data.property) {
          this.propertyData = response.data.data.property
          console.log('Successfully fetched property data:', this.propertyData)
        } else {
          console.error('Failed to fetch property details:', response.data?.message || 'Unknown error')
          // Fall back to passed property data
          this.propertyData = this.property
        }
      } catch (error) {
        console.error('Error fetching property details:', error)
        // Fall back to passed property data
        this.propertyData = this.property
      } finally {
        this.loadingProperty = false
      }
    },

    async generateReferenceNumber() {
      try {
        const formData = new FormData()
        formData.append('function', 'generateReferenceNumber')

        const response = await execute(formData, landrate)

        if (response.data && response.data.success) {
          return response.data.data.referenceNumber
        } else {
          // Fallback to frontend generation if backend fails
          return 'UPD-' + Date.now().toString(36).toUpperCase()
        }
      } catch (error) {
        console.error('Error generating reference number:', error)
        // Fallback to frontend generation
        return 'UPD-' + Date.now().toString(36).toUpperCase()
      }
    },

    async submitUpdate() {
      if (!this.hasUpdates) {
        alert('Please select at least one update type')
        return
      }

      this.loading = true

      try {
        // Generate reference number from backend
        console.log('Generating reference number...')
        const referenceNumber = await this.generateReferenceNumber()
        console.log('Generated reference number:', referenceNumber)

        // Prepare form data for API submission
        const formData = new FormData()
        formData.append('function', 'submitPropertyUpdate')
        formData.append('propertyId', this.currentProperty.id)
        formData.append('lrNumber', this.currentProperty.LRNumber)
        formData.append('referenceNumber', referenceNumber)

        // Convert update types to the format expected by backend
        formData.append('updateTypes', JSON.stringify(this.updateTypes))

        // Current property data
        formData.append('currentData', JSON.stringify(this.currentProperty))

        // Requested updates
        formData.append('requestedData', JSON.stringify(this.updateForm))

        // Verified owner information
        formData.append('verifiedOwner', JSON.stringify(this.verifiedOwner))

        console.log('Submitting property update request...')
        console.log('Property ID:', this.currentProperty.id)
        console.log('LR Number:', this.currentProperty.LRNumber)
        console.log('Reference Number:', referenceNumber)
        console.log('Update Types:', this.updateTypes)
        console.log('Update Form Data:', this.updateForm)
        console.log('Verified Owner:', this.verifiedOwner)

        // Call the actual API
        const response = await execute(formData, landrate)

        console.log('API Response:', response)

        if (response.data && response.data.success) {
          // API call successful
          const submissionData = {
            referenceNumber: response.data.data.referenceNumber || referenceNumber,
            propertyId: this.currentProperty.id,
            lrNumber: this.currentProperty.LRNumber,
            updateTypes: this.updateTypes,
            currentData: this.currentProperty,
            requestedData: this.updateForm,
            verifiedOwner: this.verifiedOwner,
            uploadedDocuments: this.uploadedDocuments,
            submittedAt: new Date().toISOString(),
            status: response.data.data.status || 'pending',
            documentFolder: response.data.data.documentFolder,
            message: response.data.message
          }

          // Now handle document uploads if any
          if (this.uploadedDocuments.length > 0) {
            console.log('Uploading documents...')
            await this.uploadDocuments(referenceNumber)
          }

          // Clear any saved draft since we've successfully submitted
          localStorage.removeItem(`property_update_draft_${this.currentProperty.id}`)

          console.log('Submission successful, emitting form-submitted event')
          this.$emit('form-submitted', submissionData)
        } else {
          throw new Error(response.data?.message || 'Failed to submit update request')
        }

      } catch (error) {
        console.error('Submission error:', error)

        let errorMessage = 'Error submitting update request. Please try again.'

        // Handle specific error cases
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || errorMessage
        } else if (error.message) {
          errorMessage = error.message
        }

        alert(errorMessage)
      } finally {
        this.loading = false
      }
    },

    // Handle document uploads
    async uploadDocuments(referenceNumber) {
      try {
        console.log('Uploading documents for reference:', referenceNumber)

        for (const document of this.uploadedDocuments) {
          const docFormData = new FormData()
          docFormData.append('function', 'uploadSupportingDocument')
          docFormData.append('referenceNumber', referenceNumber)
          docFormData.append('document', document.file)
          docFormData.append('documentCategory', this.getDocumentCategory(document.name))

          console.log('Uploading document:', document.name)

          const uploadResponse = await execute(docFormData, landrate)

          if (!uploadResponse.data.success) {
            console.warn('Failed to upload document:', document.name, uploadResponse.data.message)
          } else {
            console.log('Successfully uploaded document:', document.name)
          }
        }
      } catch (error) {
        console.error('Error uploading documents:', error)
        // Don't fail the entire submission for document upload errors
      }
    },

    getDocumentCategory(fileName) {
      const name = fileName.toLowerCase()

      if (name.includes('title') || name.includes('deed')) return 'title_deed'
      if (name.includes('id') || name.includes('identity')) return 'id_copy'
      if (name.includes('passport')) return 'passport_copy'
      if (name.includes('transfer')) return 'transfer_document'
      if (name.includes('photo') || name.includes('image') || name.includes('jpg') || name.includes('jpeg') || name.includes('png')) return 'improvement_photo'

      return 'other'
    },

    saveAsDraft() {
      // Save form data to localStorage for later
      const draftData = {
        propertyId: this.currentProperty.id,
        updateTypes: this.updateTypes,
        updateForm: this.updateForm,
        savedAt: new Date().toISOString()
      }

      localStorage.setItem(`property_update_draft_${this.currentProperty.id}`, JSON.stringify(draftData))
      alert('Draft saved successfully!')
    },

    loadDraft() {
      const draftData = localStorage.getItem(`property_update_draft_${this.currentProperty.id}`)
      if (draftData) {
        const draft = JSON.parse(draftData)
        this.updateTypes = draft.updateTypes
        this.updateForm = draft.updateForm
        console.log('Loaded draft data:', draft)
      }
    },

    handleDocumentUpload(files) {
      this.uploadedDocuments = files
      console.log('Documents updated:', files)
    },

    formatDate(dateString) {
      if (!dateString) return null
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatCurrency(value) {
      if (!value) return '0'
      return parseFloat(value).toLocaleString('en-US')
    }
  },

  mounted() {
    // Fetch the latest property data
    this.fetchPropertyDetails()

    // Load any saved draft
    this.loadDraft()

    // Log property data for debugging
    console.log('Property data received:', this.property)
    console.log('Verified owner data:', this.verifiedOwner)
  }
}
</script>

<style scoped>
.property-update-form {
  max-width: 1200px;
  margin: 0 auto;
}

.form-header h4 {
  color: #2c3e50;
  font-weight: 600;
}

.section-header {
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.current-info-section {
  background: #ffffff;
  padding: 25px;
  border-radius: 8px;
  margin-bottom: 30px;
  border: 1px solid #e9ecef;
}

.section-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 15px;
  margin-bottom: 25px;
}

.section-header h5 {
  color: #2c3e50;
  font-weight: 600;
  font-size: 20px;
}

.update-form-section {
  background: white;
  padding: 25px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.info-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  height: 100%;
  transition: all 0.3s ease;
}

.info-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.info-card .card-header {
  background: #f8f9fa;
  padding: 12px 15px;
  border-bottom: 1px solid #e9ecef;
}

.info-card .card-header h6 {
  margin: 0;
  color: #2c3e50;
  font-weight: 600;
  font-size: 15px;
}

.info-card .card-body {
  padding: 15px;
  background: white;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f8f9fa;
}

.info-item:last-child {
  border-bottom: none;
}

.info-item .label {
  font-weight: 500;
  color: #6c757d;
  font-size: 13px;
  flex: 0 0 auto;
  margin-right: 15px;
}

.info-item .value {
  color: #2c3e50;
  font-size: 14px;
  text-align: right;
  flex: 1;
  font-weight: 400;
  word-break: break-word;
}

.form-section {
  margin-bottom: 25px;
  padding: 20px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
}

.section-title {
  color: #2c3e50;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
}

.update-type-selection .form-check {
  padding: 15px 15px 15px 40px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 12px;
  transition: all 0.3s ease;
  background: white;
  position: relative;
}

.update-type-selection .form-check:hover {
  border-color: #0066cc;
  background: #f8f9fa;
}

.update-type-selection .form-check-input {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.update-type-selection .form-check-label {
  cursor: pointer;
  display: block;
  margin-left: 0;
}

.update-type-selection .form-check-input:checked + .form-check-label {
  color: #0066cc;
}

.update-type-selection .form-check-input:checked ~ * {
  color: #0066cc;
}

.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
  font-size: 14px;
}

.form-control {
  border-radius: 4px;
  border: 1px solid #ced4da;
  padding: 8px 12px;
  transition: all 0.3s ease;
  font-size: 14px;
}

.form-control:focus {
  border-color: #0066cc;
  box-shadow: 0 0 0 0.2rem rgba(0,102,204,.25);
}

.form-actions {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid #e9ecef;
}

.btn {
  border-radius: 6px;
  padding: 12px 20px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.btn-success {
  background: linear-gradient(45deg, #28a745, #20c997);
  border: none;
}

.btn-success:hover:not(:disabled) {
  box-shadow: 0 5px 15px rgba(40,167,69,0.4);
}

.badge {
  font-size: 12px;
  padding: 4px 8px;
  font-weight: 500;
  border-radius: 4px;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.text-danger {
  color: #dc3545 !important;
}

.text-primary {
  color: #0066cc !important;
}

.btn {
  border-radius: 4px;
  padding: 10px 20px;
  font-weight: 500;
  transition: all 0.3s ease;
  font-size: 14px;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn-success {
  background: #28a745;
  border: none;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
  box-shadow: 0 4px 12px rgba(40,167,69,0.3);
}

.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-secondary:hover {
  background: #6c757d;
  color: white;
}

.btn-outline-primary {
  color: #0066cc;
  border-color: #0066cc;
}

.btn-outline-primary:hover {
  background: #0066cc;
  color: white;
}

/* Additional Styling for Better Visibility */
.property-update-form {
  max-width: 1200px;
  margin: 0 auto;
  color: #2c3e50;
}

.form-header h4 {
  color: #2c3e50;
  font-weight: 600;
  font-size: 24px;
}

.form-header p {
  color: #6c757d;
  font-size: 14px;
}

.property-info-cards {
  margin-top: 20px;
}

/* Remove the dark label badges */
.info-item .label {
  background: none;
  color: #6c757d;
  padding: 0;
  border-radius: 0;
  font-weight: 500;
}

/* Financial values styling */
.info-item .value.text-danger {
  font-size: 15px;
  font-weight: 600;
}

.info-item .value.text-primary {
  font-size: 15px;
  font-weight: 600;
}

/* Form improvements */
.form-actions {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

/* Spinner styling */
.spinner-border {
  width: 2.5rem;
  height: 2.5rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Alert styling */
.alert {
  border-radius: 4px;
  font-size: 14px;
}

.alert-info {
  background-color: #e7f3ff;
  border-color: #b6d4fe;
  color: #004085;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

@media (max-width: 768px) {
  .info-item {
    flex-direction: column;
    text-align: left;
  }

  .info-item .value {
    text-align: left;
    margin-top: 5px;
  }

  .form-section {
    padding: 15px;
  }

  .form-actions .col-md-4 {
    margin-bottom: 10px;
  }

  .current-info-section,
  .update-form-section {
    padding: 15px;
  }
}
</style>